/**
 * The Altar's day (altar redesign 2026-10-27): today's horoscope, the
 * intention, the painted room and whether the first-visit rite is due. One
 * place loads them and every action goes through it, so the rite and the
 * everyday Altar never disagree about what is current.
 */
import { ref, computed } from 'vue'
import { store } from '@/store'
import {
  getPersonalHoroscope, customizePersonalHoroscope, getPersonalAffirmation, customizePersonalAffirmation,
  setPersonalAffirmation, getAltarSpace, compileAltarPrompt, generateAltarImage, markAltarRiteDone,
  markHoroscopeOpened,
} from '@/api-service'

export function useAltarToday () {
  const horoState = ref('loading')   // loading | reading | needs_birthdate | unavailable
  const horoscope = ref(null)        // { title, reading_text, date, focus }
  const sky = ref(null)              // { sign, line }
  const chart = ref(null)            // { sun, moon, rising }
  const intention = ref(null)        // { id, text, is_custom, created_at }
  const intentionLoaded = ref(false)
  const altar = ref(null)            // the altar-space payload
  const altarLoaded = ref(false)

  const imageUrl = computed(() => (altar.value && altar.value.selected_image_url) || '')
  const painted = computed(() => !!imageUrl.value)

  async function loadHoroscope () {
    if (!store.session_id) return
    if (!horoscope.value) horoState.value = 'loading'
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const d = await getPersonalHoroscope(store.session_id)
        chart.value = d.chart || chart.value
        sky.value = d.sky || null
        if (d.state === 'reading' && d.horoscope) { horoscope.value = d.horoscope; horoState.value = 'reading' }
        else if (d.state === 'needs_birthdate') horoState.value = 'needs_birthdate'
        else horoState.value = 'unavailable'
        return
      } catch (e) {
        // One quiet retry (a cut-off call should not leave the sky empty all visit).
        if (attempt === 0) await new Promise((r) => setTimeout(r, 800))
      }
    }
    horoState.value = 'unavailable'
  }

  async function loadIntention () {
    if (!store.session_id) return
    try {
      const d = await getPersonalAffirmation(store.session_id)
      intention.value = d.affirmation || null
    } catch (e) { /* */ } finally { intentionLoaded.value = true }
  }

  async function loadAltar () {
    if (!store.session_id) return
    try { altar.value = await getAltarSpace(store.session_id) } catch (e) { /* */ } finally { altarLoaded.value = true }
  }

  function loadAll () { return Promise.all([loadHoroscope(), loadIntention(), loadAltar()]) }

  /** Mystic AIda's offer, drawn on the server from today's horoscope. It becomes current. */
  async function offerIntention () {
    const d = await customizePersonalAffirmation(store.session_id, '', undefined, true)
    if (d && d.affirmation) intention.value = d.affirmation
    return d && d.affirmation
  }

  /** Their own words, kept exactly. Returns { ok, rejected?, reason? }. */
  async function keepOwnWords (text) {
    const d = await setPersonalAffirmation(store.session_id, text)
    if (d && d.ok && d.affirmation) intention.value = d.affirmation
    return d || { ok: false }
  }

  async function retune (focus, words) {
    const d = await customizePersonalHoroscope(store.session_id, (focus || '').trim(), words)
    if (d && d.ok && d.horoscope) {
      horoscope.value = d.horoscope
      horoState.value = 'reading'
      if (d.sky) sky.value = d.sky
      window.dispatchEvent(new CustomEvent('horoscope-updated', { detail: { horoscope: d.horoscope } }))
    }
    return d
  }

  /**
   * Paint the Altar from the chosen facets: compile the description from them
   * (the server adds the seeker's signs), then paint. Returns
   * { ok, url?, reason?, words? }. The first painting is free.
   */
  async function paint (facets) {
    try {
      const c = await compileAltarPrompt(store.session_id, facets)
      const prompt = (c && c.prompt) || ''
      const d = await generateAltarImage(store.session_id, { ...facets, compiled_prompt: prompt })
      if (d && d.ok === false) {
        if (d.images) altar.value = d
        return { ok: false, reason: d.reason || 'failed', words: d.words || '' }
      }
      altar.value = d
      if (typeof d.balance === 'number') store.querent_carat_balance = d.balance
      const url = d.selected_image_url || ''
      window.dispatchEvent(new CustomEvent('altar-image-updated', { detail: { url, subject: 'altar' } }))
      return { ok: !!url, url }
    } catch (e) {
      return { ok: false, reason: 'failed' }
    }
  }

  // The Journal keeps a horoscope only once it was read (journal API §6):
  // tell the server once per date, when the sky is on screen.
  const openedDates = new Set()
  function markOpened () {
    const h = horoscope.value
    if (!h || !store.session_id) return
    const key = h.date || 'today'
    if (openedDates.has(key)) return
    openedDates.add(key)
    markHoroscopeOpened(store.session_id, h.date)
  }

  /**
   * Record the rite on the server (once; a second call keeps the first time).
   * `announce: false` at "Get started": the welcome is seen, so leaving
   * mid-rite never replays it (prod 2026-10-30), but the Sun's ceremony
   * still waits for the rite's real end.
   */
  async function riteDone ({ announce = true } = {}) {
    try {
      const d = await markAltarRiteDone(store.session_id)
      if (altar.value) altar.value = { ...altar.value, rite: (d && d.rite) || { due: false } }
    } catch (e) { /* the rite replays next time; nothing is lost */ }
    if (!announce) return
    // The Quest journey: the Sun's ceremony waits for the rite's end (useQuestReveals).
    try { window.dispatchEvent(new CustomEvent('altar-ritual-done')) } catch (e) { /* */ }
  }

  // A horoscope reshaped elsewhere (the assistant's chat): it becomes current.
  function setHoroscope (h) {
    if (!h) return
    horoscope.value = h
    horoState.value = 'reading'
  }

  function setAltarImage (url) {
    if (!url) return
    altar.value = { ...(altar.value || {}), selected_image_url: url }
  }

  return {
    horoState, horoscope, sky, chart, intention, intentionLoaded, altar, altarLoaded, imageUrl, painted,
    loadHoroscope, loadIntention, loadAltar, loadAll, offerIntention, keepOwnWords, retune, paint, riteDone, markOpened,
    setAltarImage, setHoroscope,
  }
}
